/**
 * The staff inbox (GLOSSARY "Inbox", #141): pure helpers for the inbox store
 * (stores/inbox.js), the bell and the inbox view. The inbox itself is on the
 * server (GET /api/notifications), the same on every device; nothing here
 * keeps it in localStorage.
 */

const positiveInt = (value) => {
  const n = Number(value)
  return Number.isInteger(n) && n > 0 ? n : null
}

/**
 * Set the home screen app badge to the unread count (navigator.setAppBadge,
 * including iOS installed PWAs), or clear it at zero. Never throws.
 * @returns {Promise<'set'|'cleared'|'unsupported'>}
 */
export async function applyAppBadge(count, nav = typeof navigator !== 'undefined' ? navigator : undefined) {
  if (!nav || typeof nav.setAppBadge !== 'function') return 'unsupported'
  try {
    if (count > 0) {
      await nav.setAppBadge(count)
      return 'set'
    }
    if (typeof nav.clearAppBadge === 'function') await nav.clearAppBadge()
    else await nav.setAppBadge(0)
    return 'cleared'
  } catch {
    // Refused outside an installed app or without notification permission
    return 'unsupported'
  }
}

const newestFirst = (a, b) => (b.createdAt - a.createdAt) || (b.id - a.id)

/** Current items plus incoming ones (a refresh or the next page): one per id, incoming wins, newest first. */
export function mergeInboxItems(current, incoming) {
  const byId = new Map((current || []).map(n => [n.id, n]))
  for (const n of incoming || []) byId.set(n.id, n)
  return [...byId.values()].sort(newestFirst)
}

const oldestFirst = (a, b) => (a.createdAt - b.createdAt) || (a.id - b.id)

/**
 * What the sticky banner shows (#142): the notifications awaiting this staff
 * member's acknowledgement, oldest first. The server's list (GET
 * /api/notifications awaitingAcknowledgement) says what awaits, whatever inbox
 * page it is on; a loaded copy that says it can no longer be acknowledged
 * (acknowledged here, or closed) takes it off at once, since that never goes
 * back. An awaiting one is shown from its loaded copy when there is one.
 * @param {object[]} awaiting the server's awaitingAcknowledgement
 * @param {object[]} items the loaded inbox items
 */
export function pendingAcknowledgements(awaiting, items) {
  const loaded = new Map((items || []).map(n => [n.id, n]))
  return (awaiting || [])
    .map(n => loaded.get(n.id) || n)
    .filter(n => n.canAcknowledge)
    .sort(oldestFirst)
}

/** Items with one marked read at `readAt` (the first read time stays). Does not change the input. */
export function withItemRead(items, notificationId, readAt) {
  return (items || []).map(n => (n.id === notificationId && !n.readAt ? { ...n, readAt } : n))
}

/** The notification (and the push delivery that was tapped) from the inbox route /inbox/:notificationId?delivery=. */
export function inboxDeepLink(route) {
  return {
    notificationId: positiveInt(route?.params?.notificationId),
    deliveryId: positiveInt(route?.query?.delivery)
  }
}

/** The bell's count: blank at zero, capped at 99+. */
export function unreadLabel(count) {
  if (!count || count < 1) return ''
  return count > 99 ? '99+' : String(count)
}

const PENDING_KEY = 'pendingInboxTarget'
const sessionStore = () => (typeof sessionStorage !== 'undefined' ? sessionStorage : undefined)

/**
 * A tapped push opens /inbox/:id, but a locked or signed-out app sends the
 * staff member to the PIN or login screen first, which then goes home. The
 * router guard remembers the inbox target (per tab, sessionStorage) before
 * that redirect...
 */
export function rememberInboxTarget(to, storage = sessionStore()) {
  if (to?.name !== 'Inbox' || !positiveInt(to?.params?.notificationId)) return
  try { storage?.setItem(PENDING_KEY, to.fullPath) } catch { /* private mode: lost, the inbox still has it */ }
}

/**
 * ...and takes it, once, on the next page the staff member reaches: the path to
 * go to instead, or null. Reaching the inbox some other way drops it.
 */
export function takeInboxTarget(to, storage = sessionStore()) {
  let pending = null
  try {
    pending = storage?.getItem(PENDING_KEY) ?? null
    if (pending) storage.removeItem(PENDING_KEY)
  } catch {
    return null
  }
  return to?.name === 'Inbox' ? null : pending
}
